import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isFeatureEnabled } from '../config/app'
import { customAuthService } from '../services/cognitoCustom'
import { clearUserData } from '../store/slices/userSlice'
import { clearCart } from '../store/slices/cartSlice'
import { clearAnonymousUserData, clearAllUserData } from '../utils/storageManager'

export interface AuthUser {
  username: string
  isAuthenticated: boolean
  tokens?: {
    accessToken?: string
    idToken?: string
    refreshToken?: string
  }
}

export interface UseCustomAuthReturn {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean

  // OTP Sign Up Flow
  otpSignUp: (username: string) => Promise<{ success: boolean; error?: string; data?: any }>
  otpConfirmSignUp: (
    generatedUsername: string,
    confirmationCode: string,
    session: string,
    originalUsername: string,
  ) => Promise<{ success: boolean; error?: string; data?: any }>

  // Custom Auth Flow (Sign In)
  customAuthInitiate: (
    username: string,
  ) => Promise<{ success: boolean; error?: string; data?: any }>
  customAuthChallenge: (
    session: string,
    deliveryMedium: 'email' | 'phone_number',
    username: string,
  ) => Promise<{ success: boolean; error?: string; data?: any }>
  customAuthOTP: (
    session: string,
    otpCode: string,
    username: string,
  ) => Promise<{ success: boolean; error?: string; data?: any }>
  customAuthComplete: (
    username: string,
    otpCode: string,
  ) => Promise<{ success: boolean; error?: string; data?: any }>

  // Refresh Token Flow
  refreshTokenAuth: (
    username: string,
    refreshToken: string,
  ) => Promise<{ success: boolean; error?: string; data?: any }>

  // Utility functions
  signOut: () => Promise<void>
  clearAuth: () => void
}

export const useCustomAuth = (): UseCustomAuthReturn => {
  const dispatch = useDispatch()
  const [user, setUserWithLogging] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Note: setUserWithLogging is used directly in the code

  // Helper function to check if JWT token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      return payload.exp < currentTime
    } catch (error) {
      return true
    }
  }

  // Restore user state from localStorage on mount
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken')
    const idToken = localStorage.getItem('idToken')
    const refreshToken = localStorage.getItem('refreshToken')
    const username = localStorage.getItem('username')

    if (accessToken && username) {
      // Check if token is expired
      if (isTokenExpired(accessToken)) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('idToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('username')
        return
      }

      const restoredUser: AuthUser = {
        username,
        isAuthenticated: true,
        tokens: {
          accessToken,
          idToken: idToken || undefined,
          refreshToken: refreshToken || undefined,
        },
      }
      setUserWithLogging(restoredUser)
    }
  }, []) // Empty dependency array - only run on mount

  // Note: User state changes are handled by the component

  // Check if Cognito is enabled
  const isCognitoEnabled = isFeatureEnabled('enableCognito')

  // OTP Sign Up Flow
  const otpSignUp = useCallback(
    async (username: string) => {
      if (!isCognitoEnabled) {
        return { success: false, error: 'Authentication is not enabled' }
      }

      setIsLoading(true)
      try {
        const result = await customAuthService.otpSignUp({ username })
        return {
          success: result.success,
          error: result.error,
          data: result.data,
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'An unexpected error occurred' }
      } finally {
        setIsLoading(false)
      }
    },
    [isCognitoEnabled],
  )

  // OTP Confirm Sign Up Flow
  const otpConfirmSignUp = useCallback(
    async (
      generatedUsername: string,
      confirmationCode: string,
      session: string,
      originalUsername: string,
    ) => {
      if (!isCognitoEnabled) {
        return { success: false, error: 'Authentication is not enabled' }
      }

      setIsLoading(true)
      try {
        const result = await customAuthService.otpConfirmSignUp({
          generatedUsername,
          confirmationCode,
          session,
          originalUsername,
        })

        if (result.success && result.tokens) {
          // Clear anonymous user data (cart, wishlist, etc.) on successful login
          clearAnonymousUserData()
          dispatch(clearCart())
          
          setUserWithLogging({
            username: generatedUsername,
            isAuthenticated: true,
            tokens: result.tokens,
          })
        }

        return {
          success: result.success,
          error: result.error,
          data: result.data,
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'An unexpected error occurred' }
      } finally {
        setIsLoading(false)
      }
    },
    [isCognitoEnabled, dispatch],
  )

  // Custom Auth Initiate
  const customAuthInitiate = useCallback(
    async (username: string) => {
      if (!isCognitoEnabled) {
        return { success: false, error: 'Authentication is not enabled' }
      }

      setIsLoading(true)
      try {
        const result = await customAuthService.customAuthInitiate({ username })
        return {
          success: result.success,
          error: result.error,
          data: result.data,
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'An unexpected error occurred' }
      } finally {
        setIsLoading(false)
      }
    },
    [isCognitoEnabled],
  )

  // Custom Auth Challenge
  const customAuthChallenge = useCallback(
    async (session: string, deliveryMedium: 'email' | 'phone_number', username: string) => {
      if (!isCognitoEnabled) {
        return { success: false, error: 'Authentication is not enabled' }
      }

      setIsLoading(true)
      try {
        const result = await customAuthService.customAuthChallenge({
          session,
          deliveryMedium,
          username,
        })
        return {
          success: result.success,
          error: result.error,
          data: result.data,
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'An unexpected error occurred' }
      } finally {
        setIsLoading(false)
      }
    },
    [isCognitoEnabled],
  )

  // Custom Auth OTP
  const customAuthOTP = useCallback(
    async (session: string, otpCode: string, username: string) => {
      if (!isCognitoEnabled) {
        return { success: false, error: 'Authentication is not enabled' }
      }

      // Starting custom auth OTP flow

      setIsLoading(true)
      try {
        const result = await customAuthService.customAuthOTP({ session, otpCode, username })
        if (result.success && result.tokens) {
          // Clear anonymous user data (cart, wishlist, etc.) on successful login
          clearAnonymousUserData()
          dispatch(clearCart())
          
          const userData = {
            username,
            isAuthenticated: true,
            tokens: result.tokens,
          }
          setUserWithLogging(userData)
        }

        return {
          success: result.success,
          error: result.error,
          data: result.data,
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'An unexpected error occurred' }
      } finally {
        setIsLoading(false)
      }
    },
    [isCognitoEnabled, dispatch],
  )

  // Custom Auth Complete (convenience method)
  const customAuthComplete = useCallback(
    async (username: string, otpCode: string) => {
      if (!isCognitoEnabled) {
        return { success: false, error: 'Authentication is not enabled' }
      }

      setIsLoading(true)
      try {
        const result = await customAuthService.customAuthComplete({ username, otpCode })

        if (result.success && result.tokens) {
          // Clear anonymous user data (cart, wishlist, etc.) on successful login
          clearAnonymousUserData()
          dispatch(clearCart())
          
          setUserWithLogging({
            username,
            isAuthenticated: true,
            tokens: result.tokens,
          })
        }

        return {
          success: result.success,
          error: result.error,
          data: result.data,
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'An unexpected error occurred' }
      } finally {
        setIsLoading(false)
      }
    },
    [isCognitoEnabled, dispatch],
  )

  // Refresh Token Auth
  const refreshTokenAuth = useCallback(
    async (username: string, refreshToken: string) => {
      if (!isCognitoEnabled) {
        return { success: false, error: 'Authentication is not enabled' }
      }

      setIsLoading(true)
      try {
        const result = await customAuthService.refreshTokenAuth({ username, refreshToken })

        if (result.success && result.tokens) {
          setUserWithLogging({
            username,
            isAuthenticated: true,
            tokens: result.tokens,
          })
        }

        return {
          success: result.success,
          error: result.error,
          data: result.data,
        }
      } catch (error: any) {
        return { success: false, error: error.message || 'An unexpected error occurred' }
      } finally {
        setIsLoading(false)
      }
    },
    [isCognitoEnabled],
  )

  // Sign out
  const signOut = useCallback(async () => {
    try {
      // Отримуємо access token з поточного користувача
      const accessToken = user?.tokens?.accessToken

      if (accessToken) {
        // Викликаємо Cognito API для виходу
        const result = await customAuthService.signOut(accessToken)
        if (!result.success) {
        }
      }

      // Очищаємо локальний стан Cognito
      setUserWithLogging(null)

      // Очищаємо Redux стан користувача
      dispatch(clearUserData())
      
      // Очищаємо корзину
      dispatch(clearCart())

      // Очищаємо всі дані користувача з localStorage
      clearAllUserData()

    } catch (error) {
      // Навіть якщо Cognito API не спрацював, очищаємо локальний стан
      setUserWithLogging(null)
      dispatch(clearUserData())
      dispatch(clearCart())
      clearAllUserData()
    }
  }, [user?.tokens?.accessToken, dispatch])

  // Clear auth
  const clearAuth = useCallback(() => {
    setUserWithLogging(null)
  }, [])

  const returnValue = {
    user,
    isLoading,
    isAuthenticated: !!user?.isAuthenticated,
    otpSignUp,
    otpConfirmSignUp,
    customAuthInitiate,
    customAuthChallenge,
    customAuthOTP,
    customAuthComplete,
    refreshTokenAuth,
    signOut,
    clearAuth,
  }


  return returnValue
}
